import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaRegHeart, FaTrash, FaCode, FaCheck, FaTimes, FaChevronLeft, FaChevronRight, FaCopy, FaDownload, FaGlobe, FaLock, FaSpinner, FaSearch, FaSearchMinus, FaClipboard, FaShare, FaCrown, FaRecycle, FaExternalLinkAlt } from 'react-icons/fa';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';
import ConfirmDialog from './ConfirmDialog';
import NumberSelectionDialog from './NumberSelectionDialog';
import { useLocation, Link } from 'react-router-dom';
import { getEmojisFromActiveSpices, getSpiceIdsFromPrompt, getBasePromptOnly, buildPromptWithSpices } from './SpiceRack';
import { buildFullPromptForAdmin } from '../utils/promptBuilder';

// Component to display admin prompt with exact DALL-E reconstruction
const AdminPromptDisplay = ({ image }) => {
  const [fullPrompt, setFullPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const reconstructPrompt = async () => {
      setIsLoading(true);
      try {
        console.log('[ADMIN] Starting prompt reconstruction:', {
          imageId: image.id,
          basePrompt: image.basePrompt,
          spiceIds: image.spiceIds,
          prompt: image.prompt
        });

        if (image.basePrompt && image.spiceIds && image.spiceIds.length > 0) {
          console.log('[ADMIN] Reconstructing with spices...');
          const reconstructedPrompt = await buildFullPromptForAdmin(image.basePrompt, image.spiceIds);
          console.log('[ADMIN] Reconstruction result:', reconstructedPrompt);
          setFullPrompt(reconstructedPrompt);
        } else if (image.basePrompt) {
          // No spices, just show base prompt
          console.log('[ADMIN] No spices, using base prompt');
          setFullPrompt(image.basePrompt);
        } else {
          // Fallback to stored prompt
          console.log('[ADMIN] Using fallback prompt');
          setFullPrompt(image.prompt || 'No prompt available');
        }
      } catch (error) {
        console.error('[ADMIN] Error reconstructing prompt:', error);
        setFullPrompt(image.prompt || image.basePrompt || 'No prompt available');
      } finally {
        setIsLoading(false);
      }
    };

    reconstructPrompt();
  }, [image.id, image.basePrompt, image.spiceIds, image.prompt]);

  if (isLoading) {
    return (
      <div className="text-white/80 flex items-center gap-2">
        <FaSpinner className="animate-spin w-4 h-4" />
        <span>Reconstructing prompt...</span>
      </div>
    );
  }

  return (
    <div className="text-white/80">
      {fullPrompt}
    </div>
  );
};

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0
  })
};

const ImageCarousel = ({ 
  images,
  currentIndex,
  onClose, 
  onNext,
  onPrev,
  onToggleFavorite,
  onDelete,
  favorites,
  userId,
  onTogglePublic,
  onCopyToPrompt,
  isAdminView = false,
  hidePrompt = false,
  onRegenerate
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const location = useLocation();
  const isPublicPage = location.pathname === '/public';
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPublicConfirm, setShowPublicConfirm] = useState(false);
  const [showPublicSuccess, setShowPublicSuccess] = useState(false);
  const [showLikeSuccess, setShowLikeSuccess] = useState(false);
  const [[page, direction], setPage] = useState([currentIndex, 0]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const [isHeartLoading, setIsHeartLoading] = useState(false);
  const [localFavorited, setLocalFavorited] = useState(() => favorites?.has?.(images[currentIndex]?.id));
  const [localCount, setLocalCount] = useState(images[currentIndex]?.favoriteCount || 0);
  const [showDownloadingDialog, setShowDownloadingDialog] = useState(false);
  const [localPublicStatus, setLocalPublicStatus] = useState(() => images[currentIndex]?.public);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [dragStartTime, setDragStartTime] = useState(0);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const [showFloatingHeart, setShowFloatingHeart] = useState(false);
  const [showNumberSelection, setShowNumberSelection] = useState(false);
  
  // Add ref to track optimistic updates
  const optimisticUpdateRef = useRef({
    count: null,
    favorited: null
  });

  // Add this at the top with other state
  const isAnyDialogOpen = showDeleteConfirm || showPublicConfirm || showDownloadingDialog || showNumberSelection;

  // Reset zoom and position when changing images
  useEffect(() => {
    setIsZoomed(false);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, [page]);

  const handleRegenerate = () => {
    setShowNumberSelection(true);
  };

  const handleNumberSelected = (numImages) => {
    if (onRegenerate) {
      const image = images[page];
      const basePrompt = image.basePrompt || image.prompt || '';
      const spiceIds = image.spiceIds || [];
      onRegenerate(basePrompt, spiceIds, numImages, image);
      setShowNumberSelection(false);
      onClose(); // Close carousel after starting regeneration
    }
  };

  // Modify handleZoomToggle to prevent zooming when dialogs are open
  const handleZoomToggle = () => {
    if (showDeleteConfirm || showPublicConfirm || showDownloadingDialog || showNumberSelection) return;
    
    if (isZoomed) {
      // When zooming out, always reset to original position
      setIsZoomed(false);
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      // When zooming in
      setIsZoomed(true);
      setScale(2.5);
    }
  };

  // Modify handleMouseDown to prevent dragging when dialogs are open
  const handleMouseDown = (e) => {
    if (isZoomed && !(showDeleteConfirm || showPublicConfirm || showDownloadingDialog)) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setDragStartTime(Date.now());
      setDragStartPosition({ ...position });
    }
  };

  const handleMouseMove = (e) => {
    if (isZoomed && isDragging) {
      e.preventDefault();
      e.stopPropagation();
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      
      const newPosition = {
        x: dragStartPosition.x + dx,
        y: dragStartPosition.y + dy
      };
      
      setPosition(newPosition);
      setLastPosition(newPosition);
    }
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      
      const dragDuration = Date.now() - dragStartTime;
      const dx = Math.abs(position.x - dragStartPosition.x);
      const dy = Math.abs(position.y - dragStartPosition.y);
      
      // If it was a quick click with minimal movement, treat as a click
      if (dragDuration < 200 && dx < 5 && dy < 5) {
        setIsZoomed(false);
        setScale(1);
        setPosition({ x: 0, y: 0 });
        setLastPosition({ x: 0, y: 0 });
      }
      
      setIsDragging(false);
    }
  };

  useEffect(() => {
    // Add global mouse up handler
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  // Update page when currentIndex changes
  useEffect(() => {
    setPage([currentIndex, 0]);
  }, [currentIndex]);

  // Safety check - if no images or invalid index, close modal
  useEffect(() => {
    if (!images?.length || currentIndex < 0 || currentIndex >= images.length) {
      onClose();
    }
  }, [images, currentIndex, onClose]);

  // Update initial favorite state and count when carousel loads
  useEffect(() => {
    if (!isHeartLoading) {  // Only update if we're not in the middle of a heart action
      const currentImage = images[currentIndex];
      const isFavorited = favorites?.has?.(currentImage?.id);
      const currentCount = currentImage?.favoriteCount || 0;

      // Only update if we don't have an optimistic update pending
      if (optimisticUpdateRef.current.count === null) {
        setLocalCount(currentCount);
        setLocalFavorited(isFavorited);
      }
    }
  }, [currentIndex, images, favorites, isHeartLoading]);

  // Update state when changing pages
  useEffect(() => {
    if (!isHeartLoading) {
      const currentImage = images[page];
      const isFavorited = favorites?.has?.(currentImage?.id);
      const currentCount = currentImage?.favoriteCount || 0;

      // Only update if we don't have an optimistic update pending
      if (optimisticUpdateRef.current.count === null) {
        if (isFavorited !== localFavorited) {
          setLocalFavorited(isFavorited);
        }
        if (currentCount !== localCount) {
          setLocalCount(currentCount);
        }
      }
    }
  }, [page, images, favorites, isHeartLoading, localCount, localFavorited]);

  // Update initial public status when carousel loads or currentIndex changes
  useEffect(() => {
    setLocalPublicStatus(images[currentIndex]?.public);
  }, [currentIndex, images]);

  // Update local public status when changing pages
  useEffect(() => {
    setLocalPublicStatus(images[page]?.public);
  }, [page, images]);

  // If no valid images/index, don't render
  if (!images?.length || page < 0 || page >= images.length) {
    return null;
  }

  const handleNext = () => {
    onNext?.();
    setPage([page + 1 >= images.length ? 0 : page + 1, 1]);
  };

  const handlePrevious = () => {
    onPrev?.();
    setPage([page - 1 < 0 ? images.length - 1 : page - 1, -1]);
  };

  const handleCopyPrompt = async () => {
    try {
      const image = images[page];

      let basePrompt = '';
      let spiceIds = [];
      let fullPrompt = '';

      // Check if we have the new database fields
      if (image.basePrompt && image.spiceIds) {
        basePrompt = image.basePrompt;
        spiceIds = image.spiceIds;
      } else if (image.prompt) {
        // For legacy images, extract the base prompt and spice IDs from the full prompt
        spiceIds = getSpiceIdsFromPrompt(image.prompt);
        basePrompt = getBasePromptOnly(image.prompt);
      } else {
        basePrompt = '';
        spiceIds = [];
      }
      
      await navigator.clipboard.writeText(basePrompt);
      
      // Create image data with the extracted information
      const imageDataForCallback = {
        ...image,
        basePrompt: basePrompt,
        spiceIds: spiceIds
      };
      
      onCopyToPrompt?.(imageDataForCallback);
      
      setCopiedPrompt(true);
      setShowCopyConfirm(true);
      
      // Create a promise that resolves after the animation
      await new Promise((resolve) => {
        const timer = setTimeout(() => {
          setShowCopyConfirm(false);
          
          // Wait for exit animation before changing icon back
          setTimeout(() => {
            setCopiedPrompt(false);
            resolve();
          }, 200);
        }, 3000);
      });

    } catch (err) {
      console.error('❌ [COPY] Failed to copy text:', err);
    }
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    
    setIsDownloading(true);
    // Make sure delete confirmation is not shown
    setShowDeleteConfirm(false);
    try {
      const image = images[page];
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email read:images"
        }
      });

      // Show downloading dialog
      setShowDownloadingDialog(true);

      // Fetch the high-res version from the API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/images/${image.id}/download`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download image');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `retro_${image.id}_hd.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    } finally {
      setIsDownloading(false);
      setShowDownloadingDialog(false);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    if (isHeartLoading) return;
    
    setIsHeartLoading(true);
    
    // Get current values before updating
    const wasAlreadyFavorited = localFavorited;
    const previousCount = localCount;
    
    try {
      // Get auth token
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email read:images write:images"
        }
      });

      // Optimistically update UI immediately
      const newFavorited = !wasAlreadyFavorited;
      const newCount = newFavorited ? previousCount + 1 : Math.max(0, previousCount - 1);
      
      // Track optimistic update
      optimisticUpdateRef.current = {
        count: newCount,
        favorited: newFavorited
      };
      
      // Update state immediately
      setLocalFavorited(newFavorited);
      setLocalCount(newCount);

      // Trigger floating heart animation if favoriting
      if (newFavorited) {
        setShowFloatingHeart(true);
        setTimeout(() => setShowFloatingHeart(false), 1000);
      }
      
      // Make the API call
      console.log('[FAVORITE] Making API call:', {
        url: `${process.env.REACT_APP_API_URL}/api/favorites/${images[page].id}`,
        method: newFavorited ? 'POST' : 'DELETE',
        imageId: images[page].id,
        environment: process.env.NODE_ENV,
        apiUrl: process.env.REACT_APP_API_URL
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/favorites/${images[page].id}`, {
        method: newFavorited ? 'POST' : 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[FAVORITE] API response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[FAVORITE] API Error:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText,
          url: response.url
        });
        throw new Error(`Failed to update favorite status: ${response.status} ${response.statusText} - ${errorText}`);
      }

      // Call the parent handler if it exists
      if (typeof onToggleFavorite === 'function') {
        await onToggleFavorite(images[page].id);
      }

      // After successful API call, show confirmation
      setShowLikeSuccess(true);
      setTimeout(() => setShowLikeSuccess(false), 2000);

      // Keep the optimistic update active for a bit longer to prevent flashing
      setTimeout(() => {
        // Only clear if we're still on the same image and the values match what we expect
        if (images[page]?.id === images[page]?.id && 
            optimisticUpdateRef.current.favorited === newFavorited &&
            optimisticUpdateRef.current.count === newCount) {
          optimisticUpdateRef.current = {
            count: null,
            favorited: null
          };
        }
      }, 500);

    } catch (error) {
      // Revert on error
      setLocalFavorited(wasAlreadyFavorited);
      setLocalCount(previousCount);
      
      console.error('[FAVORITE] Full error details:', {
        error: error.message,
        stack: error.stack,
        imageId: images[page]?.id,
        apiUrl: process.env.REACT_APP_API_URL,
        environment: process.env.NODE_ENV
      });
      
      // Show user-friendly error with technical details in development
      const errorMessage = process.env.NODE_ENV === 'development' 
        ? `Failed to update favorite status: ${error.message}`
        : 'Failed to update favorite status';
      
      toast.error(errorMessage);
    } finally {
      setIsHeartLoading(false);
    }
  };

  // Add event listener for keyboard navigation
  useEffect(() => {
    const handleKeyboard = (e) => {
      switch(e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [onClose, handleNext, handlePrevious]);

  const handleTogglePublic = async () => {
    try {
      const newPublicStatus = !localPublicStatus;
      
      // Get auth token
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email read:images write:images"
        }
      });

      // Close the confirmation dialog immediately
      setShowPublicConfirm(false);
      
      // Update local state immediately for better UX
      setLocalPublicStatus(newPublicStatus);

      // Make the API call directly
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/images/${images[page].id}/toggle-public`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isPublic: newPublicStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update public status');
      }

      // Call the parent handler if it exists
      if (typeof onTogglePublic === 'function') {
        await onTogglePublic(images[page].id, newPublicStatus);
      }

      // Show success dialog instead of toast
      setShowPublicSuccess(true);
      setTimeout(() => setShowPublicSuccess(false), 2000);
    } catch (error) {
      // Revert on error
      setLocalPublicStatus(localPublicStatus);
      console.error('[RETRO] ❌ Error toggling public status:', {
        imageId: images[page].id,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      toast.error('Failed to update public status');
    }
  };

  const handlePublicToggle = async () => {
    if (localPublicStatus) {
      // If it's already public, show confirmation before making private
      setShowPublicConfirm(true);
      setShowDeleteConfirm(false); // Make sure delete dialog is closed
    } else {
      // If it's private, show confirmation before making public
      setShowPublicConfirm(true);
      setShowDeleteConfirm(false);
    }
  };

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Main content container */}
      <div className="relative w-full h-full flex flex-col items-center justify-center">
        {/* Confirmation Dialogs */}
        <div className="fixed inset-0 z-[100]" style={{ pointerEvents: 'none' }}>
          <AnimatePresence mode="wait">
            {showCopyConfirm && (
              <div style={{ pointerEvents: 'auto' }} key="copy-confirm">
                <ConfirmDialog
                  isOpen={true}
                  onClose={() => setShowCopyConfirm(false)}
                  type="copy"
                  isModal={true}
                />
              </div>
            )}
            {showPublicSuccess && (
              <div style={{ pointerEvents: 'auto' }} key="public-success">
                <ConfirmDialog
                  isOpen={true}
                  onClose={() => setShowPublicSuccess(false)}
                  type="public-status"
                  message={localPublicStatus ? "Image is now public" : "Image is now private"}
                  isModal={true}
                />
              </div>
            )}
            {showLikeSuccess && (
              <div style={{ pointerEvents: 'auto' }} key="like-success">
                <ConfirmDialog
                  isOpen={true}
                  onClose={() => setShowLikeSuccess(false)}
                  type="favorite"
                  message={localFavorited ? "Added to likes" : "Removed from likes"}
                  isModal={true}
                />
              </div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showDeleteConfirm && (
              <div style={{ pointerEvents: 'auto' }}>
                <ConfirmDialog
                  isOpen={showDeleteConfirm}
                  onClose={() => {
                    setShowDeleteConfirm(false);
                    setIsZoomed(false);
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                  }}
                  onConfirm={() => {
                    onDelete(images[page].id);
                    setShowDeleteConfirm(false);
                    setIsZoomed(false);
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                  }}
                  message="Delete this image? This cannot be undone."
                  type="delete"
                  isModal={true}
                />
              </div>
            )}
            {showPublicConfirm && (
              <div style={{ pointerEvents: 'auto' }}>
                <ConfirmDialog
                  isOpen={showPublicConfirm}
                  onClose={() => {
                    setShowPublicConfirm(false);
                    setIsZoomed(false);
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                  }}
                  onConfirm={handleTogglePublic}
                  message={localPublicStatus ? 
                    "Make this image private?" : 
                    "Make this image public? Anyone will be able to see it."
                  }
                  title={localPublicStatus ? "Make Private?" : "Make Public?"}
                  type="publish"
                  isModal={true}
                />
              </div>
            )}
            {showDownloadingDialog && (
              <div style={{ pointerEvents: 'auto' }}>
                <ConfirmDialog
                  isOpen={showDownloadingDialog}
                  onClose={() => {}}
                  type="downloading"
                  isModal={true}
                />
              </div>
            )}
            {showNumberSelection && (
              <div style={{ pointerEvents: 'auto' }}>
                <NumberSelectionDialog
                  isOpen={showNumberSelection}
                  onClose={() => {
                    setShowNumberSelection(false);
                    setIsZoomed(false);
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                  }}
                  onSelect={handleNumberSelected}
                />
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Microscope overlay - non-interactive layer */}
        {isZoomed && (
          <div 
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              maskImage: 'radial-gradient(circle at center, transparent 40%, black 60%)',
              WebkitMaskImage: 'radial-gradient(circle at center, transparent 40%, black 60%)',
              background: 'radial-gradient(circle at center, transparent 30%, rgba(0,0,0,0.8) 60%)',
              mixBlendMode: 'multiply',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)'
            }}
          />
        )}

        {/* Close button - moved to top right corner */}
        <button
          className={`absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors z-[70] ${isAnyDialogOpen ? 'opacity-0' : 'opacity-100'}`}
          onClick={onClose}
        >
          <FaTimes className="w-5 h-5 text-white" />
        </button>

        {/* Top action bar - adjusted z-index and mobile spacing */}
        <div className={`absolute top-4 left-4 right-20 sm:right-16 flex justify-between items-start sm:items-center px-0 sm:px-4 z-[60] transition-opacity duration-200 ${isAnyDialogOpen ? 'opacity-0' : 'opacity-100'}`}>
          {/* Left group - User actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={handleFavoriteClick}
              className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors relative"
              disabled={isHeartLoading}
            >
              {/* Floating heart animation */}
              <AnimatePresence>
                {showFloatingHeart && (
                  <motion.div
                    initial={{ opacity: 1, scale: 1, y: 0 }}
                    animate={{ opacity: 0, scale: 1.5, y: -20 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="absolute left-3 pointer-events-none"
                  >
                    <FaHeart className="w-5 h-5 text-red-500" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Static heart with bounce animation */}
              <motion.div
                animate={{ 
                  scale: localFavorited ? [1, 1.3, 1] : 1,
                  rotate: localFavorited ? [0, -10, 10, 0] : 0
                }}
                transition={{ duration: 0.4 }}
              >
                {isHeartLoading ? (
                  <FaSpinner className="w-5 h-5 animate-spin" />
                ) : localFavorited ? (
                  <FaHeart className="w-5 h-5 text-red-500" />
                ) : (
                  <FaRegHeart className="w-5 h-5" />
                )}
              </motion.div>
              {localCount > 0 && (
                <motion.span 
                  key={localCount}
                  initial={{ rotateX: -90 }}
                  animate={{ rotateX: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className={`text-[14px] font-['VT323'] hidden sm:inline ${localFavorited ? 'text-red-500' : ''}`}
                >
                  {localCount}
                </motion.span>
              )}
            </motion.button>
            
            {/* Only show public/private toggle if not on public page - hide on mobile */}
            {!isPublicPage && (
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePublicToggle();
                }}
                whileHover={{ scale: 1.1 }}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
              >
                {localPublicStatus ? (
                  <>
                    <FaGlobe className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-['VT323'] text-[18px]">Public</span>
                  </>
                ) : (
                  <>
                    <FaShare className="w-5 h-5 text-white" />
                    <span className="text-white font-['VT323'] text-[18px]">Make Public</span>
                  </>
                )}
              </motion.button>
            )}
          </div>

          {/* Right group - Image actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Regenerate button - icon only on mobile */}
            {onRegenerate && (
              <motion.button
                onClick={handleRegenerate}
                whileHover={{ scale: 1.1 }}
                className="p-2 sm:px-3 sm:py-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
              >
                <FaRecycle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                <span className="text-white font-['VT323'] text-[18px] hidden sm:inline sm:ml-2">Regenerate</span>
              </motion.button>
            )}

            {/* Copy and Download buttons - hide on mobile, show on desktop */}
            {!isPublicPage && (
              <motion.button
                onClick={handleCopyPrompt}
                whileHover={{ scale: 1.1 }}
                className="hidden sm:block p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
              >
                {copiedPrompt ? (
                  <FaCheck className="w-5 h-5 text-white" />
                ) : (
                  <FaClipboard className="w-5 h-5 text-white" />
                )}
              </motion.button>
            )}
            
            {!isPublicPage && (
              <motion.button
                onClick={handleDownload}
                whileHover={{ scale: 1.1 }}
                className="hidden sm:block p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <FaSpinner className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <FaDownload className="w-5 h-5 text-white" />
                )}
              </motion.button>
            )}
            
            {/* Delete button - hide on mobile, show on desktop */}
            {(() => {
              const currentImage = images[page];
              
              // Check if user owns this image by comparing email with creator field
              const imageUserId = currentImage.userId || currentImage.user_id || currentImage.createdBy || currentImage.user?.id || currentImage.createdById;
              const userOwnsImage = (imageUserId === userId) || (currentImage.creator === user?.email);
              const showDelete = !isPublicPage && userOwnsImage;
              
              return showDelete && (
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(true);
                    setShowPublicConfirm(false);
                  }}
                  whileHover={{ scale: 1.1 }}
                  className="hidden sm:block p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors text-white"
                >
                  <FaTrash className="w-5 h-5" />
                </motion.button>
              );
            })()}
          </div>
        </div>

        {/* Navigation chevrons */}
        <div className={`absolute left-4 top-1/2 -translate-y-1/2 z-50 transition-opacity duration-200 ${isAnyDialogOpen ? 'opacity-0' : 'opacity-100'}`}>
          <motion.button
            className="p-3 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaChevronLeft className="w-6 h-6 text-white" />
          </motion.button>
        </div>

        <div className={`absolute right-4 top-1/2 -translate-y-1/2 z-50 transition-opacity duration-200 ${isAnyDialogOpen ? 'opacity-0' : 'opacity-100'}`}>
          <motion.button
            className="p-3 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaChevronRight className="w-6 h-6 text-white" />
          </motion.button>
        </div>

        {/* Main image container - lower z-index */}
        <motion.div
          className="relative w-full h-full flex items-center justify-center overflow-hidden"
          style={{
            zIndex: 40
          }}
        >
          <motion.div
            className="relative"
            style={{
              cursor: isZoomed ? 'grab' : 'zoom-in',
              width: '200vw',
              height: '200vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <motion.img
              ref={imageRef}
              key={page}
              src={images[page].url}
              alt={images[page].prompt}
              className="max-h-[80vh] max-w-[85vw] object-contain select-none"
              onClick={handleZoomToggle}
              style={{
                scale: scale,
                cursor: isZoomed ? 'grab' : 'zoom-in',
                userSelect: 'none'
              }}
              drag={isZoomed}
              dragConstraints={{
                left: -1000,
                right: 1000,
                top: -1000,
                bottom: 1000
              }}
              dragElastic={0}
              dragMomentum={false}
              whileDrag={{ cursor: 'grabbing' }}
              initial={{ opacity: 0, scale: 0.9, x: 0, y: 0 }}
              animate={{ 
                opacity: 1, 
                scale,
                x: isZoomed ? undefined : 0,
                y: isZoomed ? undefined : 0
              }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{
                type: 'spring',
                duration: 0.2,
                bounce: 0
              }}
              onDragEnd={() => {
                if (!isZoomed) {
                  setPosition({ x: 0, y: 0 });
                }
              }}
            />
          </motion.div>

          {/* Mobile action buttons - show at bottom on mobile */}
          <div className={`sm:hidden absolute bottom-20 left-0 right-0 px-4 z-50 flex justify-center gap-2 ${isAnyDialogOpen ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}>
            {/* Copy button */}
            {!isPublicPage && (
              <motion.button
                onClick={handleCopyPrompt}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 rounded-full bg-black/60 hover:bg-black/80 transition-colors backdrop-blur-sm"
              >
                {copiedPrompt ? (
                  <FaCheck className="w-5 h-5 text-white" />
                ) : (
                  <FaClipboard className="w-5 h-5 text-white" />
                )}
              </motion.button>
            )}
            
            {/* Download button */}
            {!isPublicPage && (
              <motion.button
                onClick={handleDownload}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 rounded-full bg-black/60 hover:bg-black/80 transition-colors backdrop-blur-sm"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <FaSpinner className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <FaDownload className="w-5 h-5 text-white" />
                )}
              </motion.button>
            )}
            
            {/* Public/Private toggle */}
            {!isPublicPage && (
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePublicToggle();
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 rounded-full bg-black/60 hover:bg-black/80 transition-colors backdrop-blur-sm"
              >
                {localPublicStatus ? (
                  <FaGlobe className="w-5 h-5 text-green-400" />
                ) : (
                  <FaShare className="w-5 h-5 text-white" />
                )}
              </motion.button>
            )}
            
            {/* Delete button */}
            {(() => {
              const currentImage = images[page];
              const imageUserId = currentImage.userId || currentImage.user_id || currentImage.createdBy || currentImage.user?.id || currentImage.createdById;
              const userOwnsImage = (imageUserId === userId) || (currentImage.creator === user?.email);
              const showDelete = !isPublicPage && userOwnsImage;
              
              return showDelete && (
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(true);
                    setShowPublicConfirm(false);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-2 rounded-full bg-black/60 hover:bg-black/80 transition-colors backdrop-blur-sm"
                >
                  <FaTrash className="w-5 h-5 text-white" />
                </motion.button>
              );
            })()}
          </div>

          {/* Image info overlay */}
          <div className={`absolute bottom-0 left-0 right-0 px-4 sm:px-6 py-3 text-white font-['VT323'] z-50 flex flex-col items-center ${isAnyDialogOpen ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}>
            <div className="flex flex-col items-center gap-2">
              {/* Show source image reference if this was recreated from gallery */}
              {images[page].sourceImageId && (
                <Link 
                  to={`/image/${images[page].sourceImageId}`}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white/80 hover:text-white text-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span>Recreated from Blatant Space</span>
                  <FaExternalLinkAlt className="w-3 h-3" />
                </Link>
              )}
              
              {/* Show prompt based on view type - hide if image was recreated from gallery */}
              {!hidePrompt && !images[page].sourceImageId && (isAdminView || !isPublicPage || images[page].userId === userId) && (
                <div className="text-base sm:text-lg text-center opacity-80 max-w-[85vw] whitespace-pre-wrap break-words">
                  {isAdminView ? (
                    /* Admin view - show full prompt and user info */
                    <div className="flex flex-col items-center gap-2">
                      <AdminPromptDisplay image={images[page]} />
                    </div>
                  ) : location.pathname === '/bs' ? (
                    <div className="flex flex-col items-center gap-1">
                      <div className="text-sm text-white/60">
                        {images[page].user?.email}
                      </div>
                      <div className="text-white/80">
                        {(() => {
                          const image = images[page];
                          if (image.basePrompt) {
                            return image.basePrompt;
                          } else if (image.prompt) {
                            // For legacy images, extract base prompt without spices
                            return getBasePromptOnly(image.prompt);
                          }
                          return '';
                        })()}
                      </div>
                    </div>
                  ) : (
                    (() => {
                      const image = images[page];
                      if (image.basePrompt) {
                        return image.basePrompt;
                      } else if (image.prompt) {
                        // For legacy images, extract base prompt without spices
                        return getBasePromptOnly(image.prompt);
                      }
                      return '';
                    })()
                  )}
                </div>
              )}
              
              {/* Style emojis - don't show in admin view (since we show full prompt) or if hidePrompt is true */}
              {!hidePrompt && !isAdminView && (!isPublicPage || images[page].userId === userId) && (() => {
                const styleEmojis = getEmojisFromActiveSpices(images[page].spiceIds || []);
                const uniqueEmojis = [...new Set(styleEmojis)];
                
                return styleEmojis.length > 0 && (
                  <div className="flex items-center gap-1 opacity-70">
                    {uniqueEmojis.map((emoji, index) => (
                      <span key={`${emoji}-${index}`} className="text-lg">
                        {emoji}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="text-xs sm:text-sm opacity-60 mt-1 flex items-center gap-2 flex-wrap">
              {isAdminView && (
                <>
                  <span>{images[page].user?.email || images[page].creator || images[page].createdBy || 'Unknown User'}</span>
                  <span className="opacity-40">•</span>
                </>
              )}
              <span>{images[page].createdAt && format(new Date(images[page].createdAt), 'MMMM d, yyyy')}</span>
              <span className="opacity-40">•</span>
              <span>{images[page].createdAt && formatDistanceToNow(new Date(images[page].createdAt), { addSuffix: true })}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  return createPortal(modalContent, document.body);
};

export default ImageCarousel;